// SPDX-License-Identifier: GPL-3.0-or-later
// The plunger power bar (THEME.md section 7, follow-up F10). It shows how far the plunger is pulled,
// from 0 to 100. The value is the pull of the engine plunger, not a guess of the page: the engine
// pulls the plunger in about 500 ms while the player holds the plunger control, and a release
// fires it. The plunger rests at its park position, so the bar reads 0 at rest and 100 at the full
// stroke. The bar is a gauge. It decides nothing, and the force is never random.
// Reduced motion: the bar moves in 4 steps (0, 25, 50, 75, 100), not in a smooth sweep.

import type { ItemFrame, RenderFrame } from "../src/shared/contracts.ts";
import { type BarSlot, setBar, type TextSlot } from "./dom.ts";

/** The stroke of a plunger that the table spec leaves open (the engine default, in table units). */
export const DEFAULT_PLUNGER_STROKE = 80;
/** The rest position of a plunger that the table spec leaves open (the engine default, 0 to 1 of the stroke). */
export const DEFAULT_PARK_POSITION = 0.5 / 3;
/** The number of steps of the bar under reduced motion. */
export const REDUCED_STEPS = 4;

function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0;
}

/**
 * The pull of the plunger as a fraction of its stroke, from 0 (forward) to 1 (fully back).
 * A frame holds only the items that changed. A frame with no plunger item keeps `previous`.
 */
export function pullFraction(
  items: readonly ItemFrame[],
  name: string,
  stroke: number,
  previous: number,
): number {
  const item = items.find((i) => i.kind === "plunger" && i.name === name);
  if (item === undefined || item.offset === undefined) return previous;
  return stroke > 0 ? clamp01(item.offset / stroke) : 0;
}

/**
 * The power of a pull, from 0 to 1. The plunger rests at its park position, not at the forward
 * limit, so the pull that the player makes starts there: the rest position is power 0, and the
 * full stroke is power 1.
 */
export function powerFraction(pull: number, park: number): number {
  const rest = clamp01(park);
  return rest >= 1 ? 0 : clamp01((clamp01(pull) - rest) / (1 - rest));
}

/** The number on the bar: 0 to 100. Under reduced motion it moves in steps of 25. */
export function powerPercent(fraction: number, reducedMotion: boolean): number {
  const f = clamp01(fraction);
  if (!reducedMotion) return Math.round(f * 100);
  return Math.round(f * REDUCED_STEPS) * (100 / REDUCED_STEPS);
}

/** The nodes of the bar. */
export interface PowerSlots {
  readonly bar: BarSlot;
  readonly text: TextSlot;
}

/** The plunger of the table. */
export interface PlungerInfo {
  readonly name: string;
  readonly stroke?: number;
  /** The rest position, 0 to 1 of the stroke. */
  readonly parkPosition?: number;
}

/** The power bar. */
export interface PowerGauge {
  /** Read the plunger pull from a frame. Call it at each frame. */
  update(frame: RenderFrame): void;
  setReducedMotion(reduced: boolean): void;
  /** Set the bar to 0. Call it when a new game takes the place of the old one. */
  reset(): void;
}

/** Make the bar. It starts at 0. */
export function createPowerGauge(slots: PowerSlots, plunger: PlungerInfo): PowerGauge {
  const stroke = plunger.stroke ?? DEFAULT_PLUNGER_STROKE;
  const park = plunger.parkPosition ?? DEFAULT_PARK_POSITION;
  /** The pull as the engine reports it: 0 is the forward limit. The plunger starts at rest. */
  let pull = park;
  let reduced = false;
  let shown = -1;

  const draw = (): void => {
    const percent = powerPercent(powerFraction(pull, park), reduced);
    if (percent === shown) return;
    shown = percent;
    setBar(slots.bar, percent / 100);
    slots.text.textContent = `POWER ${percent}`;
  };

  draw();
  return {
    update(frame) {
      pull = pullFraction(frame.items, plunger.name, stroke, pull);
      draw();
    },
    setReducedMotion(next) {
      reduced = next;
      draw();
    },
    reset() {
      pull = park;
      draw();
    },
  };
}
